import React from 'react';
import { useNavigate } from 'react-router-dom';
import { InstagramLogo, FacebookLogo, LinkedinLogo, WhatsappLogo, Envelope } from '@phosphor-icons/react';

const NOVA_LOGO = "https://customer-assets.emergentagent.com/job_nova-fit-meals/artifacts/8r5uywp4_Nova%20Foods.png";
const MELOUTE_URL = 'https://www.meloute.com/about-company.html';

const socials = [
  { name: 'Instagram', href: 'https://instagram.com/thenovafoods', Icon: InstagramLogo },
  { name: 'Facebook', href: 'https://facebook.com/thenovafoods', Icon: FacebookLogo },
  { name: 'LinkedIn', href: 'https://linkedin.com/company/thenovafoods', Icon: LinkedinLogo },
  { name: 'WhatsApp', href: 'https://wa.me/919902993067', Icon: WhatsappLogo },
  { name: 'Email', href: 'mailto:support@thenovafoods.com', Icon: Envelope },
];

const PremiumFooter = () => {
  const navigate = useNavigate();
  const scrollToContact = (e) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#0F2A20] text-stone-300 pt-20 md:pt-28 px-6 pb-10" data-testid="footer" role="contentinfo">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 mb-16">
          {/* Column 1: Brand */}
          <div>
            <button onClick={() => navigate('/')} className="flex items-center gap-2.5 font-heading text-2xl font-semibold text-white mb-5">
              <img src={NOVA_LOGO} alt="Nova Foods" className="h-11 w-11 rounded-lg object-cover" loading="lazy" />
              Nova Foods
            </button>
            <p className="text-stone-400 leading-relaxed text-sm max-w-xs mb-6">
              Meals designed for your fitness goals. Freshly prepared. Thoughtfully packed. Delivered on time.
            </p>
            <div className="flex items-center gap-2.5">
              {socials.map(({ name, href, Icon }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-stone-400 hover:bg-white/10 hover:text-white hover:border-white/30 hover:-translate-y-0.5 transition-all duration-300"
                  data-testid={`social-${name.toLowerCase()}`}
                >
                  <Icon size={18} weight="regular" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Services */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white mb-5">Services</p>
            <ul className="space-y-3 text-sm">
              <li><button onClick={() => navigate('/plans')} className="text-stone-400 hover:text-white transition-colors">Fitness Plans</button></li>
              <li><button onClick={() => navigate('/wellness')} className="text-stone-400 hover:text-white transition-colors" data-testid="footer-wellness">Nova Wellness</button></li>
              <li><button onClick={() => navigate('/corporate-catering')} className="text-stone-400 hover:text-white transition-colors">Corporate Catering</button></li>
              <li><button onClick={() => navigate('/bulk-orders')} className="text-stone-400 hover:text-white transition-colors" data-testid="footer-bulk-orders">Bulk Orders / House Parties</button></li>
              <li><button onClick={() => navigate('/meals')} className="text-stone-400 hover:text-white transition-colors">Healthy Meals</button></li>
              <li><button onClick={() => navigate('/plans')} className="text-stone-400 hover:text-white transition-colors">Meal Subscription</button></li>
            </ul>
          </div>

          {/* Column 3: Company */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white mb-5">Company</p>
            <ul className="space-y-3 text-sm">
              <li><a href="#services" className="text-stone-400 hover:text-white transition-colors">About Nova Foods</a></li>
              <li><a href="#contact" onClick={scrollToContact} className="text-stone-400 hover:text-white transition-colors">Contact</a></li>
              <li><button onClick={() => navigate('/privacy')} className="text-stone-400 hover:text-white transition-colors">Privacy Policy</button></li>
              <li><button onClick={() => navigate('/terms')} className="text-stone-400 hover:text-white transition-colors">Terms &amp; Conditions</button></li>
              <li><button onClick={() => navigate('/refund')} className="text-stone-400 hover:text-white transition-colors">Refund Policy</button></li>
              <li><button onClick={() => navigate('/cancellation')} className="text-stone-400 hover:text-white transition-colors">Cancellation Policy</button></li>
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white mb-5">Contact</p>
            <ul className="space-y-3 text-sm">
              <li>
                <p className="text-stone-500 text-xs mb-0.5">Email</p>
                <a href="mailto:support@thenovafoods.com" className="text-stone-300 hover:text-white transition-colors">support@thenovafoods.com</a>
              </li>
              <li>
                <p className="text-stone-500 text-xs mb-0.5">Phone</p>
                <a href="tel:+919902993067" className="text-stone-300 hover:text-white transition-colors">+91 99029 93067</a>
              </li>
              <li>
                <p className="text-stone-500 text-xs mb-0.5">Location</p>
                <p className="text-stone-300">Bangalore, Karnataka</p>
              </li>
              <li>
                <p className="text-stone-500 text-xs mb-0.5">Website</p>
                <a href="https://www.thenovafoods.com" target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-white transition-colors">www.thenovafoods.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-stone-500">© 2026 Nova Foods. All Rights Reserved.</p>
          <p className="text-sm text-stone-500">
            Powered by{' '}
            <a href={MELOUTE_URL} target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-white underline underline-offset-4 decoration-white/20 hover:decoration-white/60 transition-colors" data-testid="meloute-link">
              Meloute Technologies
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default PremiumFooter;
